import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSeo } from "@/lib/seo";
import { api } from "@/lib/api";
import { Section, CTAButton } from "@/components/shared";
import { ArrowLeft } from "lucide-react";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/articles/${slug}`).then(({ data }) => setArticle(data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo(
    article ? `${article.title} | Systems Beyond` : "Article | Systems Beyond",
    article ? article.excerpt : "Systems Beyond research updates and articles."
  );

  if (notFound) {
    return (
      <Section className="pt-40 pb-32 text-center">
        <p className="overline justify-center">404</p>
        <h1 className="mt-6 text-3xl text-ink">Article not found.</h1>
        <CTAButton to="/articles" variant="accent" className="mt-8">Back to Articles</CTAButton>
      </Section>
    );
  }

  if (!article) {
    return <Section className="pt-40 pb-32"><p className="text-dark-gray text-sm">Loading…</p></Section>;
  }

  return (
    <article>
      <Section className="pt-36 pb-12 lg:pt-44" grid>
        <Link to="/articles" className="inline-flex items-center gap-2 font-mono-sci text-xs text-dark-gray hover:text-ink transition-colors mb-10" data-testid="back-to-articles">
          <ArrowLeft size={14} /> ALL ARTICLES
        </Link>
        <div className="max-w-3xl">
          <div className="flex items-center gap-4 font-mono-sci text-[11px] text-blue-gray">
            <span>{article.category}</span><span>·</span><span>{article.date}</span><span>·</span><span>{article.reading_time}</span>
          </div>
          <h1 className="mt-6 text-4xl lg:text-6xl tracking-tighter leading-[1] font-medium text-ink">{article.title}</h1>
          {article.excerpt && <p className="mt-6 text-lg text-dark-gray leading-relaxed">{article.excerpt}</p>}
        </div>
      </Section>

      <Section className="pb-12">
        <div className="border border-silver/50 overflow-hidden">
          <img src={article.image} alt={article.title} className="w-full h-auto max-h-[460px] object-cover" />
        </div>
      </Section>

      <Section className="pb-24 lg:pb-32">
        <div className="max-w-2xl article-prose">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
          <div className="mt-14 pt-10 border-t border-silver/50">
            <p className="overline mb-4">Continue</p>
            <div className="flex flex-wrap gap-3">
              <CTAButton to="/articles" variant="secondary" testId="article-more">More Articles</CTAButton>
              <CTAButton to="/simulator" variant="accent" testId="article-simulator">Launch the Simulator</CTAButton>
            </div>
          </div>
        </div>
      </Section>
    </article>
  );
}
